import type { CareerJob } from '~/content/careers';
import { careersOpenPositions } from '~/content/careers';
import { cn } from '~/lib/cn';

function SplitHeading({ title, sticky }: { title: string; sticky?: boolean }) {
  return (
    <div
      className={cn(
        'flex items-center pb-0 lg:pb-14',
        sticky && 'lg:sticky lg:top-[150px] lg:mt-[15px]',
      )}>
      <h2 className="text-[19px] font-medium leading-[1.3] text-ink sm:text-[22px]">{title}</h2>
      <div className="ml-4 hidden h-px flex-1 bg-black-6 lg:block" />
    </div>
  );
}

export function CareersOpenPositions({ jobs }: { jobs: CareerJob[] }) {
  return (
    <section className="px-5 py-6 md:py-12 lg:px-20 lg:py-[120px] lg:pt-6 lg:pb-12">
      <div className="mx-auto grid w-full max-w-[1280px] grid-cols-1 items-start gap-7 lg:grid-cols-[0.6fr_1fr] lg:gap-10">
        <SplitHeading title={careersOpenPositions.title} />
        <div>
          {jobs.length === 0 ? (
            <p className="text-[17px] text-ink-secondary">
              No open positions right now. Check back soon.
            </p>
          ) : (
            <ul className="list-none p-0">
              {jobs.map((job) => (
                <li key={job.slug}>
                  <a
                    href={job.applyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group mt-[17px] mb-6 flex flex-col items-start text-ink transition-colors duration-150 hover:text-red-500 min-[480px]:flex-row min-[480px]:items-center">
                    <span className="text-[22px] leading-[1.3]">{job.title}</span>
                    <span className="mt-1 text-[16px] text-ink-secondary transition-colors duration-150 group-hover:text-red-500 min-[480px]:mt-0 min-[480px]:ml-2">
                      {job.location}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

export function CareersLifeSection({
  title,
  paragraphs,
  closing,
}: {
  title: string;
  paragraphs: string[];
  closing: string;
}) {
  return (
    <section className="px-5 py-16 md:py-20 lg:px-20 lg:py-[120px]">
      <div className="mx-auto grid w-full max-w-[1280px] grid-cols-1 items-start gap-7 lg:grid-cols-[0.6fr_1fr] lg:gap-10">
        <SplitHeading title={title} sticky />
        <div className="relative">
          {/* Sticky top fade matching Webflow `.text-fade-out` (desktop only).
              Uses -mt (not -mb) so the overlay sits above the text at rest and
              only fades lines as they scroll under the sticky edge. */}
          <div
            aria-hidden
            className="pointer-events-none sticky top-0 z-[5] -mt-[180px] hidden h-[180px] lg:block"
            style={{
              backgroundImage: 'linear-gradient(#fff 50%, #fff0 81%)',
            }}
          />
          {paragraphs.map((text) => (
            <p
              key={text.slice(0, 48)}
              className="mb-6 text-[24px] font-medium leading-[1.3] tracking-[-0.02em] text-ink sm:mb-9 sm:text-[38px] lg:text-[48px]">
              {text}
            </p>
          ))}
          <p className="mb-0 text-[24px] font-medium leading-[1.3] tracking-[-0.02em] text-red-500 sm:text-[38px] lg:text-[48px]">
            {closing}
          </p>
        </div>
      </div>
    </section>
  );
}
